import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowRight, Laptop } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedFortLauderdale = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Fort Lauderdale & Broward County FL</title>
        <meta name="description" content="Fort Lauderdale Lady Bird Deed attorney serving Broward County. Avoid probate, protect your South Florida home. Remote service. Call 941-206-2223." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-fort-lauderdale" />
        <meta property="og:title" content="Lady Bird Deed Attorney Fort Lauderdale & Broward County FL" />
        <meta property="og:description" content="Fort Lauderdale Lady Bird Deed attorney serving Broward County. Avoid probate, protect your South Florida home. Remote service available." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-fort-lauderdale" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Lady Bird Deed Florida", "item": "https://thelawyerlady.com/lady-bird-deed-florida" },
            { "@type": "ListItem", "position": 3, "name": "Fort Lauderdale", "item": "https://thelawyerlady.com/lady-bird-deed-fort-lauderdale" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": { "@type": "PostalAddress", "addressLocality": "Venice", "addressRegion": "FL", "addressCountry": "US" },
          "areaServed": [{ "@type": "City", "name": "Fort Lauderdale" }, { "@type": "County", "name": "Broward County" }, { "@type": "State", "name": "Florida" }],
          "priceRange": "$800-$1000"
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/lady-bird-deed-florida" className="hover:text-[#c9a87c] hover:underline transition-colors">Lady Bird Deed Florida</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Fort Lauderdale</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Serving Fort Lauderdale & Broward County FL
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, and I help South Florida homeowners protect their property with Lady Bird Deeds. My office is on the Gulf Coast in Venice, but I serve Broward County and all of Florida remotely.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-[#faf9f7]/60">
            <div className="flex items-center gap-2">
              <Laptop className="w-4 h-4 text-[#c9a87c]" />
              <span>100% Remote Service</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#c9a87c]" />
              <span>Serving Broward, Palm Beach & Miami-Dade</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">Lady Bird Deeds for South Florida Homeowners</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Fort Lauderdale and Broward County are home to hundreds of thousands of homeowners — many of them retirees who moved to South Florida and want to make sure their property passes smoothly to their children. A Lady Bird Deed is often the simplest way to do that.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              With a Lady Bird Deed, you name who will inherit your home when you pass away, but you keep complete control during your lifetime. You can sell, refinance, rent it out, or change your beneficiaries at any time. When you die, the property transfers automatically — no probate required.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Why Fort Lauderdale Homeowners Love Lady Bird Deeds</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Many of my South Florida clients have adult children who live out of state. They moved to Florida for the weather, and now they're thinking about what happens to their home when they're gone.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed makes things easy. Your kids won't have to hire a Florida probate attorney, file court paperwork, or wait months to take ownership. They'll just record your death certificate and the property is theirs. For a home worth $400,000, $500,000, or more, that can save your family thousands of dollars and months of hassle.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Remote Service — No Travel Needed</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I serve Fort Lauderdale clients entirely online. Here's how it works:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>We have a phone or video consultation to discuss your property and goals</li>
              <li>I draft your Lady Bird Deed and email it for review</li>
              <li>You sign through Florida's remote online notarization system — from your computer or tablet</li>
              <li>I record the deed with the Broward County Recorder</li>
            </ul>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              You never have to leave Fort Lauderdale or drive across the state to my Venice office. The whole process usually takes 1-2 weeks.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Cost</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My flat fee for a Lady Bird Deed is $800-$1,000. Broward County recording fees are additional — typically around $30-50. I'll give you the exact total before we start.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Serving All of South Florida</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I work with homeowners throughout South Florida, including Broward County, Palm Beach County, and Miami-Dade County. If you own property anywhere in Florida, I can help.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">statewide Lady Bird Deed services</Link>, or see how I help homeowners in <Link to="/lady-bird-deed-tampa-fl" className="text-[#c9a87c] hover:underline">Tampa</Link>, <Link to="/lady-bird-deed-orlando-fl" className="text-[#c9a87c] hover:underline">Orlando</Link>, and <Link to="/lady-bird-deed-sarasota" className="text-[#c9a87c] hover:underline">Sarasota</Link>.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Protect Your South Florida Home?</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Call 941-206-2223 or schedule a free consultation online. I'll explain how a Lady Bird Deed works and whether it's the right choice for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedFortLauderdale;
